import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

import { DataTable, Column } from "@/components/ui/data-table";
import { Edit, Eye, Trash2, Plus, Image } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RoomCategory } from "@/types/roomCategory";
import { useHotel } from "@/hooks/useHotel";
import { ReservationNavigation } from "./components/ReservationNavigation";
import { SubNavigation } from "./components/SubNavigation";
import { useRoomCategoriesFiltering } from "@/hooks/useRoomCategoriesFiltering";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import RoomCategoriesFilter from "./rooms/components/RoomCategoriesFilter";
import { useRoomCategoryDelete } from "./rooms/hooks/useCategoryDelete";
import TruncatedText from "@/components/common/TruncatedText";
import { useGetPmsSettings } from "@/components/pages/pms/hooks/pms.hooks";
import { shouldShowPmsColumns } from "@/utils/pmsVisibility";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { getRoomCategoryAccessForUser } from "./utils/roomCategoryAccess";

export const RoomsCategoriesPage = () => {
  const { hotelId, isLoading, error } = useHotel();

  if (isLoading) {
    return <div>Loading</div>;
  }

  if (error) {
    <div className="py-4 text-center text-red-600">
      Error loading hotel: {error.message}
    </div>;
  }

  return <HotelRoomCategories hotelId={hotelId!} />;
};

const HotelRoomCategories: React.FC<{ hotelId: string }> = ({ hotelId }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuthenticatedUser();
  const access = getRoomCategoryAccessForUser(userProfile);

  const subNavigationTabs = [
    { id: "rooms", label: "Rooms" },
    { id: "categories", label: "Categories" },
  ];

  const { categories, isLoading, error, pagination, filters, actions } =
    useRoomCategoriesFiltering(hotelId);

  const { handleRoomCategoryDelete, isLoading: isLoadingDeletion } =
    useRoomCategoryDelete();

  const pmsSettings = useGetPmsSettings(hotelId);
  const showPmsColumns = shouldShowPmsColumns(pmsSettings);

  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  // Selection applies to the current page only: reset on page or filter change.
  useEffect(() => {
    setSelectedKeys([]);
  }, [
    filters.currentPage,
    filters.categoryId,
    filters.amenities?.join(","),
    filters.sortField,
    filters.sortDirection,
  ]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">Loading room categories...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-red-500">Error loading room categories</div>
      </div>
    );
  }

  // Handle room deletion
  const handleDeleteRoomCategory = async (categoryId: string) => {
    if (window.confirm("Are you sure you want to delete this room category?")) {
      try {
        await handleRoomCategoryDelete(categoryId, hotelId);
      } catch (error) {
        console.error("Failed to delete room category:", error);
      }
    }
  };

  const columns: Column<RoomCategory>[] = [
    {
      key: "photo",
      header: "Photo",
      render: (_, category: RoomCategory) => (
        <div className="flex h-12 w-12 items-center justify-center rounded border border-gray-300 bg-gray-100">
          <PresignedImageWithPlaceholder
            className={"h-10 w-10 overflow-hidden object-cover"}
            photo={category.photoId}
            placeholder={<Image className="h-6 w-6 text-gray-400" />}
          />
        </div>
      ),
    },
    {
      key: "name",
      header: "Category",
      render: (value: string) => (
        <div className="text-sm font-medium text-gray-900">
          <TruncatedText limit={30}>{value}</TruncatedText>
        </div>
      ),
      sortable: true,
    },
    ...(showPmsColumns
      ? [
          {
            key: "pmsCategory",
            header: "PMS Category",
            render: (value: string) => (
              <div className="text-sm text-gray-900">{value}</div>
            ),
          },
        ]
      : []),
    {
      key: "description",
      header: "Description",
      render: (value: string) => (
        <div className="max-w-xs text-sm whitespace-normal text-gray-900">
          <TruncatedText limit={30}>{value}</TruncatedText>
        </div>
      ),
      sortable: true,
    },
    {
      key: "amenities",
      header: "Features",
      render: (amenities: string[]) => (
        <div className="text-sm whitespace-normal text-gray-900">
          {amenities.join(", ")}
        </div>
      ),
      sortable: true,
    },
    {
      key: "numberOfRooms",
      header: "Number of rooms",
      className: "text-center",
      render: (_, category: RoomCategory) => (
        <div className="text-sm text-gray-900">{category.rooms.length}</div>
      ),
      sortable: true,
    },
    {
      key: "price",
      header: "Price",
      render: (value: number) => (
        <div className="text-sm text-gray-900">{value}</div>
      ),
      sortable: true,
    },
    {
      key: "actions",
      header: "Actions",
      render: (_, category) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/reservations/rooms/categories/${category.id}`);
            }}
          >
            {access.canEdit ? (
              <Edit className="h-4 w-4 text-gray-600" />
            ) : (
              <Eye className="h-4 w-4 text-gray-600" />
            )}
          </Button>
          {access.canDelete && (
            <Button
              onClick={(event) => {
                event.stopPropagation();
                handleDeleteRoomCategory(category.id);
              }}
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={isLoadingDeletion}
            >
              <Trash2 className="h-4 w-4 text-gray-600" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const onTabChange = (tab: string) => {
    if (tab === "rooms") {
      navigate("/reservations/rooms");
    }
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-6">
      {/* Navigation Tabs */}
      <ReservationNavigation activeTab="rooms" />

      {/* Sub Navigation */}
      <SubNavigation
        activeTab={"categories"}
        onTabChange={onTabChange}
        tabs={subNavigationTabs}
      />

      {/* Filters and Add Button */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <RoomCategoriesFilter
            filters={filters}
            actions={actions}
            hotelId={hotelId}
          />
        </div>

        <Button
          variant="new_default"
          onClick={() => navigate("/reservations/rooms/categories/add")}
        >
          <Plus />
          Add category
        </Button>
      </div>

      <DataTable
        data={categories}
        columns={columns}
        loading={isLoading}
        onRowClick={(category) =>
          navigate(`/reservations/rooms/categories/${category.id}`)
        }
        getRowKey={(category) => category.id}
        selection={{ selectedKeys, onChange: setSelectedKeys }}
        pagination={{
          currentPage: pagination.currentPage,
          totalPages: pagination.totalPages,
          onPageChange: pagination.goToPage,
        }}
        sorting={{
          field: filters.sortField,
          direction: filters.sortDirection,
          onSort: actions.handleSort,
        }}
        emptyMessage="No room categories found matching your criteria"
      />
    </div>
  );
};
